/**
 * Shared SummaryCard sum field names: `{row}{Yoa|LastYoa}` + optional `Change` for split cells.
 */
import {toNumber} from '@/utils/dataUtils';
import type {BankLoanSum, LoanSummaryCardSums, VehicleLoanSum} from '@/services/auditService/interface/wp-row';

/** Prefer split right (*Change) when present; otherwise base. */
export function effectiveLoanSum(
    base: number | null | undefined,
    change: number | null | undefined,
): number {
    if (change != null) return toNumber(change);
    return toNumber(base);
}

const LEGACY_TO_CANON: Array<[string, string]> = [
    ['interestCurrChange', 'interestYoaChange'],
    ['interestLastChange', 'interestLastYoaChange'],
    ['principalNextChange', 'currentYoaChange'],
    ['principalCurrChange', 'currentLastYoaChange'],
    ['principalAfterNextToChange', 'nonCurrentYoaChange'],
    ['lastYearEndBalanceChange', 'balanceLastYoaChange'],
    ['hpInterestLastChange', 'interestLastYoaChange'],
    ['interestCurr', 'interestYoa'],
    ['interestLast', 'interestLastYoa'],
    ['principalNext', 'currentYoa'],
    ['principalCurr', 'currentLastYoa'],
    ['principalAfterNextTo', 'nonCurrentYoa'],
    ['lastYearEndBalance', 'balanceLastYoa'],
    ['hpInterestLast', 'interestLastYoa'],
    ['hpInterest', 'interestYoa'],
    ['currentLastChange', 'currentLastYoaChange'],
    ['currentChange', 'currentYoaChange'],
    ['nonCurrentChange', 'nonCurrentYoaChange'],
    ['currentLast', 'currentLastYoa'],
    ['nonCurrentLast', 'nonCurrentLastYoa'],
    ['yearEndBalanceLast', 'balanceLastYoa'],
    ['yearEndBalance', 'balanceYoa'],
];

function hasOwn(obj: object, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * Map historical Bank/Vehicle sum keys onto the shared SummaryCard names.
 * Does not promote yearEndBalanceChange / nonCurrentLastChange (retired split cells).
 */
export function normalizeLegacyLoanSums<T extends Partial<LoanSummaryCardSums>>(raw: T | null | undefined): T {
    if (!raw || typeof raw !== 'object') return (raw ?? {}) as T;
    const out: Record<string, unknown> = {...raw};
    for (const [legacy, canon] of LEGACY_TO_CANON) {
        if (!hasOwn(out, canon) && hasOwn(out, legacy) && out[legacy] !== undefined) {
            out[canon] = out[legacy];
        }
    }
    return out as T;
}

export function emptyBankLoanSums(): BankLoanSum {
    return {
        interestYoa: 0,
        currentLastYoa: 0,
        interestLastYoa: 0,
        currentYoa: 0,
        nonCurrentYoa: 0,
        interestNext: 0,
        balanceLastYoa: undefined,
        interestYoaChange: undefined,
        interestLastYoaChange: undefined,
        currentYoaChange: undefined,
        currentLastYoaChange: undefined,
        nonCurrentYoaChange: undefined,
        balanceLastYoaChange: undefined,
    };
}

export function emptyVehicleLoanSums(): VehicleLoanSum {
    return {
        currentYoa: 0,
        nonCurrentYoa: 0,
        balanceYoa: 0,
        interestYoa: 0,
        currentLastYoa: 0,
        nonCurrentLastYoa: 0,
        balanceLastYoa: 0,
        interestLastYoa: 0,
        currentYoaChange: undefined,
        currentLastYoaChange: undefined,
        nonCurrentYoaChange: undefined,
        interestYoaChange: undefined,
        interestLastYoaChange: undefined,
        balanceLastYoaChange: undefined,
    };
}

/** YOA Balance = effective Current + Non-Current (Bank Summary Card). */
export function resolveBalanceYoa(sums: Partial<LoanSummaryCardSums> | null | undefined): number {
    if (!sums) return 0;
    return effectiveLoanSum(sums.currentYoa, sums.currentYoaChange)
        + effectiveLoanSum(sums.nonCurrentYoa, sums.nonCurrentYoaChange);
}

/** lastYoa Non-Current = lastYoa Balance ? lastYoa Current. */
export function resolveNonCurrentLastYoa(
    sums: Partial<LoanSummaryCardSums> | null | undefined,
    lastYoaTotal: number,
): number {
    if (!sums) return 0;
    const currentLast = effectiveLoanSum(sums.currentLastYoa, sums.currentLastYoaChange);
    return lastYoaTotal - currentLast;
}
